import React, { useMemo, useState } from "react";
import {
  Business,
  PeopleOutline,
  Place,
  Restore,
  Search,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import { grey } from "@mui/material/colors";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import base, { listingURL } from "../../../api/apis";
import { fullName } from "../../../utils/functions";

import { MarkerIcon } from "../../Icons";

const InputField = styled(TextField)(({ theme, cuspadding }) => ({
  margin: ".7rem 0",

  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#fff",
    paddingRight: "12px",
  },
  "& .MuiOutlinedInput-root input": {
    padding: cuspadding,
  },
}));
const SearchBox = styled(Box)(({ theme }) => ({
  borderBottom: `0px solid ${grey[300]}`,
  display: "block",
}));
const SearchBoxTitle = styled(Typography)(() => ({
  margin: "0.5rem 0 0.5rem 1rem",
  fontWeight: 500,
  color: grey[600],
  fontSize: ".8rem",
}));
const StyledWrapper = styled(Box)(({ theme }) => ({
  marginTop: "5px",
  backgroundColor: "#fff",
  position: "absolute",
  padding: "8px 0",
  zIndex: 9999,
  borderRadius: "6px",
  boxShadow:
    "0 4px 16px 0 rgba(19, 19, 19, .12), 0 1px 4px 0 rgba(19, 19, 19, .08)",
}));

const industryProfLink = `https://${
  process.env.REACT_APP_URL.includes("dev") ? "testing." : ""
}industryprofessionals.com`;
const orderBPOReportLink = `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}order-bpo-report`;

export const SearchComp = ({
  hero = false,
  placeholder = "Enter an address, city, neighborhood or zip code",
  load,
  onChange,
  focus,
  searchFilter,
  results,
  agents,
  searchTerm,
  setSearchTerm,
  type,
  recent,
  iconSize = "medium",
  showCta = true,
  cuspadding,
  searchType = "buy",
  sx = {},
  setSearchLoad,
  ...rest
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const [searchFocus, setSearchFocus] = useState(false);

  const allResults = useMemo(() => {
    return results?.length !== 0
      ? results
      : JSON.parse(localStorage.getItem("places"));
  }, [results]);

  const hasMatchingResults = useMemo(() => {
    if (!searchTerm || !allResults || allResults.length === 0) {
      return false;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    return allResults.some((result) => {
      // Check if any property of the object contains the search term
      for (const key in result) {
        if (
          typeof result[key] === "string" &&
          result[key]?.toLowerCase()?.includes(lowerSearchTerm)
        ) {
          return true;
        }
      }
      return false;
    });
  }, [searchTerm, allResults]);

  const clickAway = (e) => {
    window.addEventListener("click", function (e) {
      if (document.getElementById("searchbox")?.contains(e.target)) {
        if (!searchFocus) setSearchFocus(true);
      } else {
        setSearchFocus(false);
      }
    });
  };

  const handleOnFocus = (val) => {
    setSearchFocus(true);
  };

  // get users location if they accept permission
  function getGeoLocation(e) {
    const geoAlertText =
      "Geolocation is not supported by your browser or it is disabled. Please check your settings. ";

    async function success(position) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      try {
        const { data } = await base.post("/bpo_listings/get-location/", {
          latitude,
          longitude,
        });

        handleSuggestionClick(e, { name: data?.city }, "city");
        setSearchLoad(false);
      } catch (error) {
        setSearchLoad(false);
      }
    }
    function error(message) {
      alert(geoAlertText);
      setSearchLoad(false);
    }
    if (!navigator?.geolocation) {
      alert(geoAlertText);
      setSearchLoad(false);
    } else {
      setSearchLoad(true);
      navigator.geolocation.getCurrentPosition(success, error);
    }
  }

  const handleRouteByType = (city) => {
    if (searchType === "rent")
      navigate(`/homes-for-rent/${_.startCase(city)?.toLowerCase()}`);
    else navigate(`/homes-for-sale/${_.startCase(city)?.toLowerCase()}`);
  };

  const handleSearchCta = () => {
    if (searchTerm && searchTerm?.length > 1)
      switch (searchType) {
        case "sell":
          navigate(`/sell?q=${searchTerm}`);
          break;
        case "professionals":
          window.open(
            `${industryProfLink}?city=${searchTerm}`,
            "_blank",
            "noopener,noreferrer",
          );
          break;
        case "home-estimate":
          window.open(
            `${orderBPOReportLink}?q=${searchTerm}`,
            "_blank",
            "noopener,noreferrer",
          );
          break;
        case "agent-finder":
          navigate(`/agents?q=${searchTerm}`);
          break;

        default:
          break;
      }
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    searchFilter(e);
  };

  const handleSuggestionClick = async (e, res, type) => {
    e.stopPropagation();

    if (["buy", "rent"].includes(searchType)) {
      // Upload search query
      await listingURL.post(`search-upload/`, {
        search:
          (type === "city" ? res?.address?.city : res?.address?.full) ||
          res?.name,
      });

      // Route to corresponding page
      if (res.listing === "off_market" || type !== "city")
        navigate(
          `/listing/${res?.mlsId ? res?.mlsId : res?.property_id}/details/${
            res?.listing
          }`,
        );
      else handleRouteByType(res?.address?.city || res?.name);
    } else {
      setSearchTerm(
        _.startCase(
          (type === "city" ? res?.address?.city : res?.address?.full) ||
            res?.name,
        ),
      );
    }

    // toggle off input focus
    setSearchFocus(false);
  };

  const suggestionTitle = (type, data) => {
    if (type === "address")
      return _.startCase(data?.address ? data?.address?.full : data?.name);
    else
      return data?.address
        ? `${_.startCase(data?.address?.streetName?.toLowerCase())},
            ${
              data?.address?.streetName
                ?.toLowerCase()
                .includes(data?.address?.city?.toLowerCase())
                ? ""
                : _.startCase(
                    data?.address?.city?.split(" ")?.length > 1
                      ? data?.address?.city?.toLowerCase()
                      : data?.address?.city,
                  )
            }`
        : data?.name;
  };

  // Sub Components
  const RecentSearch = ({ list = [] }) => (
    <>
      {list?.filter((item) => item?.search).length > 0 && (
        <SearchBox pb={1}>
          <SearchBoxTitle>RECENT SEARCH</SearchBoxTitle>
          {list
            ?.filter((item) => item?.search)
            ?.slice(0, 2)
            ?.map((res, index) => {
              return (
                <ListItem
                  key={index}
                  onClick={(e) =>
                    handleSuggestionClick(e, { name: res?.search }, "city")
                  }
                  button
                  sx={{
                    padding: "0.5rem 1rem",
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "25px", pt: ".4rem" }}>
                    {<Restore color="secondary" sx={{ fontSize: "1.1rem" }} />}
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="body2">
                      {_.startCase(res?.search)}
                    </Typography>
                  </ListItemText>
                </ListItem>
              );
            })}
        </SearchBox>
      )}
    </>
  );

  return (
    <Box width="100%" position={"relative"}>
      <form onSubmit={handleOnSubmit} autoComplete="off">
        {hero ? (
          <Box
            sx={{
              maxWidth: { xs: "unset", md: "604px" },
              backgroundColor: "paper.main",
              borderRadius: "0 0 6px 6px",
              overflow: "hidden",
              ...sx,
            }}
          >
            <TextField
              fullWidth
              id="searchbox"
              type="text"
              variant="outlined"
              placeholder={placeholder}
              value={searchTerm}
              onFocus={handleOnFocus}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    {["buy", "rent"].includes(searchType) ? (
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={focus}
                        disableElevation
                        size="small"
                        loading={load}
                        sx={{
                          borderRadius: "6px",
                          marginRight: "-8px",
                          width: "40px",
                          minWidth: "40px",
                          height: "40px",
                          "&:hover": {
                            bgcolor: "secondary.main",
                          },
                        }}
                      >
                        <Search color="paper" />
                      </LoadingButton>
                    ) : (
                      searchType &&
                      searchType !== "search" && (
                        <LoadingButton
                          loading={load}
                          variant="contained"
                          color="primary"
                          size={md ? "medium" : "small"}
                          onClick={handleSearchCta}
                          sx={{
                            color: "paper.main",
                            marginRight: "-8px",
                          }}
                        >
                          {searchType === "sell" && "Next"}
                          {searchType === "professionals" &&
                            "Connect with Expert"}
                          {searchType === "home-estimate" && "Get Report"}
                          {searchType === "agent-finder" &&
                            "Connect with Agent"}
                        </LoadingButton>
                      )
                    )}
                  </InputAdornment>
                ),
                sx: {
                  paddingRight: "12px",
                  borderRadius: "0px 0px 6px 6px",
                  height: "64px",
                  "& fieldset": { border: "none", top: "-4.5px" },
                  "&.Mui-focused fieldset": {
                    border: "1px solid",
                    borderRadius: "0px 0px 6px 6px",
                  },
                  "&.MuiOutlinedInput-root:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "& input": {
                    paddingLeft: hero ? "1rem" : 2,
                  },
                  "& input::placeholder": {
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  },
                  ...sx,
                },
              }}
              onBlur={clickAway}
              onChange={onChange}
              {...rest}
            />
          </Box>
        ) : (
          <InputField
            cuspadding={cuspadding}
            id="searchbox"
            type="text"
            variant="outlined"
            sx={{
              zIndex: 10,
              "& input::placeholder": {
                fontSize: "1rem",
              },
              ...sx,
            }}
            placeholder={placeholder}
            onBlur={clickAway}
            onChange={onChange}
            onFocus={handleOnFocus}
            value={searchTerm}
            fullWidth
            InputProps={{
              endAdornment: showCta && (
                <InputAdornment position="end">
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={searchFilter}
                    disableElevation
                    size="small"
                    loading={load}
                    sx={{
                      padding: cuspadding ? "6px 0" : ".7rem",
                      borderRadius: "10px",
                      marginRight: "-8px",
                      "&:hover": {
                        bgcolor: "primary.main",
                      },
                    }}
                  >
                    <Search color="paper" fontSize={iconSize} />
                  </LoadingButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      </form>

      {searchTerm
        ? searchFocus && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              {allResults?.length > 0 ? (
                <StyledWrapper
                  sx={{
                    width: { xs: "100%", sm: "86%", md: "86%", lg: "100%" },
                  }}
                >
                  <Box maxHeight="50vh" sx={{ overflowY: "auto" }}>
                    {hasMatchingResults && recent && recent?.length > 0 && (
                      <>
                        <RecentSearch list={recent} />
                        <Divider light />
                      </>
                    )}
                    {type === "city" ? (
                      <SearchBox>
                        <SearchBoxTitle variant="body2">PLACES</SearchBoxTitle>
                        {allResults?.slice(0, 4)?.map((res, index) => {
                          return (
                            <ListItem
                              button
                              key={index}
                              sx={{
                                padding: "0.5rem 1rem",
                                alignItems: "flex-start",
                              }}
                              onClick={(e) =>
                                handleSuggestionClick(e, res, type)
                              }
                            >
                              <ListItemIcon
                                sx={{ minWidth: "25px", pt: ".6rem" }}
                              >
                                {
                                  <MarkerIcon
                                    color="secondary"
                                    sx={{ fontSize: "1rem" }}
                                  />
                                }
                              </ListItemIcon>
                              <ListItemText>
                                {index === 0 ? (
                                  <>
                                    <Typography>
                                      {res?.address
                                        ? _.startCase(res?.address?.city) +
                                          ", " +
                                          _.startCase(res?.address?.state)
                                        : res?.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem" }}
                                      color={"GrayText"}
                                    >
                                      {res?.address
                                        ? _.startCase(res?.address?.city) +
                                          ", " +
                                          _.startCase(res?.address?.state) +
                                          ", USA"
                                        : res?.formatted_address}
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography>
                                      {res?.address
                                        ? _.startCase(res?.address?.city)
                                        : res?.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color={"GrayText"}
                                    >
                                      {res?.address
                                        ? _.startCase(
                                            res.address?.crossStreet?.toLowerCase(),
                                          ) +
                                          " " +
                                          _.startCase(res.address?.city) +
                                          ", " +
                                          _.startCase(res.address?.state) +
                                          ", USA"
                                        : res?.formatted_address}
                                    </Typography>
                                  </>
                                )}
                              </ListItemText>
                            </ListItem>
                          );
                        })}
                      </SearchBox>
                    ) : (
                      <SearchBox>
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                          sx={{ paddingLeft: "1rem" }}
                        >
                          <Business
                            color="secondary"
                            sx={{ fontSize: "1.5rem" }}
                          />
                          <SearchBoxTitle variant="body2">
                            ADDRESSES
                          </SearchBoxTitle>
                        </Stack>
                        {allResults
                          ?.slice(0, 3)
                          ?.filter((item) => item.listing !== "off_market")
                          ?.map((res, index) => {
                            return (
                              <ListItem
                                onClick={(e) =>
                                  handleSuggestionClick(e, res, type)
                                }
                                button
                                key={index}
                                sx={{
                                  padding: "0.5rem 1rem",
                                  alignItems: "flex-start",
                                }}
                              >
                                <ListItemText>
                                  <Typography>
                                    {suggestionTitle(type, res)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: ".9rem" }}
                                    color={"GrayText"}
                                  >
                                    {res?.address
                                      ? (_.startCase(res?.address?.city) +
                                          ", " +
                                          _.startCase(res?.address?.state) +
                                          " " +
                                          res?.address?.postalCode ??
                                        res?.address?.situszip5)
                                      : res?.formatted_address}
                                  </Typography>
                                </ListItemText>
                                {/* {res?.mls?.status && (
                                  <Chip
                                    size="small"
                                    color={
                                      res?.mls?.status?.toLowerCase() ===
                                        "sold" ||
                                      res?.mls?.status?.toLowerCase() ===
                                        "closed"
                                        ? "error"
                                        : "default"
                                    }
                                    sx={{ ml: "auto" }}
                                    label={res?.mls?.status}
                                  />
                                )} */}
                              </ListItem>
                            );
                          })}
                      </SearchBox>
                    )}

                    {/* Off-Market suggested listings */}
                    {allResults
                      ?.filter((item) => item.listing === "off_market")
                      ?.slice(0, 3)
                      ?.map((res, index) => {
                        return (
                          <ListItem
                            button
                            key={index}
                            sx={{
                              padding: "0.5rem 1rem",
                              alignItems: "flex-center",
                            }}
                            onClick={(e) => handleSuggestionClick(e, res, type)}
                          >
                            <ListItemText>
                              <Typography>
                                {suggestionTitle(type, res)}
                              </Typography>
                              <Typography variant="body2" color={"GrayText"}>
                                {res?.address
                                  ? _.startCase(
                                      res.address?.city?.toLowerCase(),
                                    ) +
                                    ", " +
                                    _.startCase(res.address?.state) +
                                    ", USA"
                                  : res?.mailingfullstreetaddress}
                              </Typography>
                            </ListItemText>
                            {/* <Chip
                              size="small"
                              label="Off Market"
                              color="secondary"
                              sx={{
                                lineHeight: "15px",
                                "& span": { mt: "2px" },
                              }}
                            /> */}
                          </ListItem>
                        );
                      })}

                    {/* Agents suggested listings */}
                    {agents && agents.length > 0 && (
                      <SearchBox>
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                          sx={{ paddingLeft: "1rem" }}
                        >
                          <PeopleOutline
                            color="secondary"
                            sx={{ fontSize: "1.5rem" }}
                          />
                          <SearchBoxTitle variant="body2">
                            AGENTS
                          </SearchBoxTitle>
                        </Stack>
                        {agents?.slice(0, 4)?.map((agent, index) => (
                          <ListItem
                            button
                            key={index}
                            sx={{
                              padding: "0.5rem 1rem",
                              alignItems: "flex-center",
                            }}
                            onClick={() => navigate(`/agents/${agent.id}`)}
                          >
                            <ListItemText>
                              <Typography>
                                {_.startCase(
                                  agent.subscription
                                    ? agent.brokerage_name
                                    : fullName(agent),
                                )}
                              </Typography>
                              <Typography variant="body2" color={"GrayText"}>
                                {agent.broker
                                  ? _.startCase(agent.brokerage_address) +
                                    ", USA"
                                  : _.startCase(agent?.city) +
                                    ", " +
                                    _.startCase(agent?.state) +
                                    ", USA"}
                              </Typography>
                            </ListItemText>
                          </ListItem>
                        ))}
                      </SearchBox>
                    )}
                  </Box>
                </StyledWrapper>
              ) : (!allResults || allResults?.length === 0) && !load ? (
                <StyledWrapper
                  sx={{
                    width: { xs: "100%", sm: "86%", md: "86%", lg: "100%" },
                  }}
                >
                  <SearchBox sx={{ padding: "0.5rem 1rem" }}>
                    <Typography textAlign={"center"}>
                      No listing found
                    </Typography>
                  </SearchBox>
                </StyledWrapper>
              ) : null}
            </motion.div>
          )
        : searchFocus && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              <StyledWrapper paddingY=".5rem" width="100%">
                <Button
                  onClick={getGeoLocation}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    textAlign: "start",
                    justifyContent: "start",
                    height: { xs: "2rem", md: "3rem" },
                    paddingLeft: "0.9rem",
                    "&:hover": {
                      bgcolor: grey[100],
                    },
                  }}
                  startIcon={<NearMeOutlinedIcon />}
                >
                  Search your Location
                </Button>

                {recent && recent?.length > 0 && (
                  <>
                    <Divider light />
                    <RecentSearch list={recent} />
                  </>
                )}
              </StyledWrapper>
            </motion.div>
          )}
    </Box>
  );
};
